<template>
  <div
    ref="mainChart"
    v-click-outside="closeDetailChart"
    :class="[
      'main-chart',
      isThumbnail ? 'chart-thumnail-item' : 'chart-detail-item',
      isDetail ? 'chart-detail__main' : '',
      isActive === item.key && !isDetail && 'chart-active'
    ]"
  >
    <div
      class="no-drag buttons"
      :class="[isThumbnail ? 'chart-thumnail-title' : 'chart-detail-title']"
    >
      <div class="chart-title">
        <div
          @click.stop="toggleDetailChart"
          v-lego-tooltip="{
            value: tooltipPerformanceData,
            lines: 8,
            position: 'top',
            moreAction: !isDetail && isThumbnail && 'clickToHide',
            timer: 1000,
            isShowHTML: true
          }"
          class="chart-title_text"
        >
          <c-label
            :tooltip="false"
            :ellipsis="true"
            :style="getStyleTitle"
            :class="isThumbnail || item.chartList.length ? 'active-title' : ''"
            >{{ item.title }}
          </c-label>
        </div>
        <div style="display: flex; align-items: center; gap: 26px">
          <div v-if="!isThumbnail && !isLog" class="dropdown-group">
            <!-- 통계방법 선택 -->

            <lego-drop-holder-static-method
              style="margin: 0 4px !important"
              ref="statisticsTypeRef"
              dropdownIcon
              :rowId="idx"
              :items="statisticsType.time"
              :disabled="false"
              :stats-types="item['lastSelectedStatisticsTypes'] ?? []"
              menuDirection="right"
              itemWidth="133"
              @confirm="changeStatisticsType($event, item, idx)"
              @click-reset="changeStatisticsType($event, item, idx)"
            >
            </lego-drop-holder-static-method>

            <!-- 차트유형 선택 -->
            <lego-drop-holder-item-icon
              :rowId="idx"
              ref="graphTypeRef"
              :btnIconType="item.chartTypeObject.iconType || 'line'"
              :btnIconName="item.chartTypeObject.iconName || 'line_chart'"
              :isDropIcon="true"
              :submenuIcon="true"
              :dropdownData="$const.CHART_TYPE"
              isChartType
              :tooltip="$t('page.performance.graphType')"
              @click="changeChartType($event, item)"
            >
            </lego-drop-holder-item-icon>

            <!-- export유형 선택 -->
            <lego-drop-holder-item-icon
              :rowId="idx"
              :btnIconType="'line'"
              :btnIconName="'download_small'"
              :isDropIcon="true"
              :submenuIcon="false"
              :dropdownData="$const.FILE_TYPE"
              :tooltip="$t('page.performance.download')"
              @click="saveFile($event, item)"
            >
            </lego-drop-holder-item-icon>
            <lego-drop-holder-item-icon
              v-if="!hideTimeSeriesChart"
              ref="btnMonitor"
              :rowId="idx"
              :btnIconType="'line'"
              :btnIconName="'monitor_icon'"
              :isDropIcon="true"
              :submenuIcon="true"
              :dropdownData="monitorType"
              :tooltip="$t('page.performance.monitoring')"
              @click="monitorAction($event, item, idx)"
            >
            </lego-drop-holder-item-icon>
          </div>
          <div
            class="chart-max-value"
            v-show="item.chartList.length && arrMaxValueChart.maxValueChart?.length"
          >
            <TooltipChartMaxValue
              :is-thumbnail="isThumbnail"
              :id="'tooltip-' + idx"
              :key="keyGenTooltip"
              :data="arrMaxValueChart"
              v-if="item.chartList.length > 1"
            />
            <div class="until" @mouseenter="onHover(item)" @mouseleave="onLeave()" v-else>
              <span>{{ item.maxValue }}</span>
              <span class="metric-unit">{{ item.metricUnit }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div v-if="item.chartList.length > 0" class="chart-detail-content" style="padding-bottom: 6px">
      <c-chart
        ref="cChartRef"
        :items="item.chartList"
        :type="item.chartType"
        :opt="isThumbnail ? itemChartOption : item.chartOption"
        :exopt="item.chartExopt"
        :zoom="item.zoom"
        cursor="default"
        :silent="true"
        :class="[isThumbnail ? 'chart-thumnail' : 'chart-detail']"
      />
    </div>
    <div v-else class="no-data">
      <lego-icon iconType="picto" icon="widget_nodata" :size="37" color="#d2d3d6" />
      {{ $t('label.noData') }}
    </div>
    <div v-if="isActive === item.key && !isDetail" class="detail-chart" :style="stylePosition">
      <DetailChart
        :is-log="isLog"
        :isDetail="true"
        :item="item"
        :idx="idx"
        :mode="mode"
        :is-thumbnail="false"
        @toggle-detail="toggleDetailChart"
        @change-statistics-type="changeStatisticsType"
        @add-widget-popup="addWidgetPopup"
        @event-add-modal="eventAddModal"
        @save-file="saveFile"
        @close-modal="closeModal"
      />
    </div>
    <TooltipPerformanceItem
      v-if="item.metric.metricKey"
      :key="item.metric.metricKey"
      :mode="mode"
      v-show="false"
      ref="tooltipPerformanceItem"
      :dataTooltip="item"
    />
  </div>
</template>

<script setup>
import TooltipPerformanceItem from '@/containers/components/TooltipPerformanceItem.vue';
import TooltipChartMaxValue from './components/TooltipChartMaxValue.vue';
import legoEventBus from '@/lego-component/utils/eventBus/eventBus';
import { EVENT_POPUP } from '@/components/constants/constants';
import {
  computed,
  inject,
  ref,
  defineEmits,
  defineProps,
  onMounted,
  onBeforeUnmount,
  watch
} from 'vue';
import i18n from '@/i18n';
import { getCurrentInstance } from 'vue';

const { $const } = getCurrentInstance().appContext.config.globalProperties;

const emit = defineEmits([
  'save-file',
  'toggle-detail',
  'toggle-detail-chart',
  'change-statistics-type',
  'add-widget-popup',
  'event-add-modal'
]);
let changeDataKey = inject('changeDataKey');
let isActive = inject('isActive');
const t = (text) => {
  return i18n.global.t(text);
};
const tooltipPerformanceItem = ref(null);
const cChartRef = ref(null);
const mainChart = ref(null);
const statisticsTypeRef = ref(null);
const graphTypeRef = ref(null);
const btnMonitor = ref(null);
const checkShowDetail = ref(false);
const statisticsType = ref({
  time: [],
  timeOrg: [
    //차트 표시 시간단위(5분,10분,30분,1시간,3시간,6시간)
    {
      value: '5',
      text: '5' + t('label.minute'),
      '3d': false,
      '7d': false,
      '14d': false
    },
    {
      value: '10',
      text: '10' + t('label.minute'),
      '3d': true,
      '7d': false,
      '14d': false
    },
    {
      value: '30',
      text: '30' + t('label.minute'),
      '3d': true,
      '7d': true,
      '14d': false
    },
    {
      value: '60',
      text: '1' + t('label.hour'),
      '3d': true,
      '7d': true,
      '14d': true
    },
    {
      value: '180',
      text: '3' + t('label.hour'),
      '3d': true,
      '7d': true,
      '14d': true
    },
    {
      value: '360',
      text: '6' + t('label.hour'),
      '3d': true,
      '7d': true,
      '14d': true
    }
  ]
});
const monitorType = ref([
  {
    txt: t('button.addEventSetting'),
    iconType: 'line',
    iconName: 'policy_setting',
    chartType: 'line',
    type: 'policySetting'
  },
  {
    txt: t('title.timeSeriesGraphAddWidget'),
    iconType: 'line',
    iconName: 'add',
    chartType: 'add',
    type: 'addWidget'
  }
]);

const arrMaxValueChart = ref([]);
const lstTooltip = ref([]);
const keyGenTooltip = ref(null);
const hideTooltip = ref(false);

const tooltipPerformanceData = computed(() => {
  if (tooltipPerformanceItem.value) return tooltipPerformanceItem.value.$el.innerHTML;
  return '';
});
const itemChartOption = computed(() => {
  let dataChartOption =
    props.item && props.item.chartOption ? _.cloneDeep(props.item.chartOption) : {};

  if (isActive.value !== '' || hideTooltip.value) {
    dataChartOption.tooltip["show"] = false
  }
  dataChartOption.legend = {
    left: '30px',
    bottom: 0,
    icon: 'circle',
    type: 'scroll',
    padding: 8,
    data: []
  };
  dataChartOption.grid.bottom = '10px';
  return dataChartOption;
});
const props = defineProps({
  item: {
    type: Object,
    default: () => {}
  },
  isThumbnail: {
    type: Boolean,
    default: false
  },
  position: {
    type: Object,
    default: () => {}
  },
  isDetail: {
    type: Boolean,
    default: false
  },
  idx: {
    type: Number,
    default: null
  },
  isLog: {
    type: Boolean,
    default: false
  },
  hideTimeSeriesChart: {
    type: Boolean,
    default: false
  },
  mode: {
    type: String,
    default: ''
  }
});
const stylePosition = computed(() => {
  if (!props.position) return;
  let top = '36px';
  if (
    Number(props.position.top.slice(0, props.position.top.length - 2)) >
      Number(props.position.height.slice(0, props.position.top.length - 2)) * 2 &&
    !props.isLog
  )
    top = '-280px';
  return `left: calc(12px - ${props.position.left}); top: ${top} `;
});
const getStyleTitle = computed(() => {
  let style = {};

  style['cursor'] = 'pointer';
  style['color'] = '#364fc7';

  if (!props.isThumbnail || !props.item.chartList.length) {
    style['cursor'] = 'default';
    style['color'] = '#2f3237';
    style['text-decoration'] = 'none';
  }

  return style;
});

watch(
  () => props.item,
  (item) => {
    if (item.tooltipChart) {
      setValArrTooltip(item);
    }
  },
  {
    deep: true
  }
);

onMounted(() => {
  legoEventBus.$on('click-outside', (isOutSide) => closeDetailChart(isOutSide));
  legoEventBus.$on(EVENT_POPUP.DATE_TIME_PICKER, (val) => { hideTooltip.value = val });

  setValArrTooltip(props.item);

  if (cChartRef.value && cChartRef.value.$refs.chart) {
    cChartRef.value.$refs.chart.chart.on('legendselectchanged', (lst) => {
      keyGenTooltip.value = Date.now();
      let legends = [];

      // get legends is enable of chart
      if (lst.selected) {
        for (const [key, value] of Object.entries(lst.selected)) {
          if (value) legends.push(key);
        }
      }

      // map legends of chart with text for show tooltip
      if (lstTooltip.value.length) {
        arrMaxValueChart.value.maxValueChart = lstTooltip.value.filter((tt) =>
          legends.includes(tt.shortName)
        );
      }
    });
  }
});

const setValArrTooltip = (item) => {
  keyGenTooltip.value = Date.now();

  lstTooltip.value = item.tooltipChart ? _.cloneDeep(item.tooltipChart.maxValueChart) : [];
  arrMaxValueChart.value = item.tooltipChart ? _.cloneDeep(item.tooltipChart) : [];
};

onBeforeUnmount(() => {
  legoEventBus.$off('click-outside', (isOutSide) => closeDetailChart(isOutSide));
  legoEventBus.$off(EVENT_POPUP.DATE_TIME_PICKER,(val) => { hideTooltip.value = val});
});

const toggleDetailChart = () => {
  legoEventBus.$emit('click-outside-datePicker');

  if (!props.isThumbnail || !props.item.chartList.length) return;
  if (isActive.value === props.item.key) changeDataKey('');
  else changeDataKey(props.item.key);
};
const closeDetailChart = (item) => {
  const isOutSide = typeof item == 'boolean' && item;

  if (checkShowDetail.value && !isOutSide) {
    checkShowDetail.value = false;
    return;
  }

  if ((props.isDetail || isOutSide) && changeDataKey) {
    changeDataKey('');
  }
};
const onHover = (item) => {
  if (!cChartRef.value || hideTooltip.value) return;
  if (isActive.value !== '' && !props.isDetail) return;

  let idx = item.chartList[0].dataIndex - 2; 
    cChartRef.value.$refs.chart.dispatchAction({
      type: 'showTip',
      seriesIndex: 0,
      dataIndex: idx
    });
 
};
const onLeave = () => {
  if (!cChartRef.value) return;

  cChartRef.value.$refs.chart.dispatchAction({
    type: 'hideTip'
  });
  cChartRef.value.$refs.chart.dispatchAction({
    type: 'showTip',
    seriesIndex: 0,
    dataIndex: -1
  });
};
const changeStatisticsType = async (selItem, item, idx) => {
  checkShowDetail.value = true;
  emit('change-statistics-type', selItem, item, idx);
};
const changeChartType = (selItem, metricItem) => {
  checkShowDetail.value = true;
  metricItem.chartExopt = selItem.chartExopt;
  metricItem.chartType = selItem.chartType;
  metricItem.chartTypeObject.iconType = selItem.iconType;
  metricItem.chartTypeObject.iconName = selItem.iconName;
  metricItem.chartTypeObject.widgetChartType = selItem.type;
};
const saveFile = (selItem, metricItem, mainChartRef = null, thumbnail = null) => {
  checkShowDetail.value = true;
  emit(
    'save-file',
    selItem,
    metricItem,
    mainChartRef !== null ? mainChartRef : mainChart.value,
    thumbnail === null ? props.isThumbnail : thumbnail
  );
};
const monitorAction = (event, item, idx, isTimeSeriesChart = false) => {
  checkShowDetail.value = true;

  if (btnMonitor.value) btnMonitor.value.idxActive = null;
  if (event.type === 'addWidget' || isTimeSeriesChart) addWidgetPopup(item, idx);
  if (event.type === 'policySetting') eventAddModal(item.metric);
};
const eventAddModal = (item) => {
  emit('event-add-modal', item);
};
const addWidgetPopup = (item, idx) => {
  emit('add-widget-popup', item, idx);
};
</script>

<style scoped lang="scss">
.chart-detail__main {
  padding: 12px 20px 20px 20px !important;
}

.no-data {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1 1 auto;
  color: rgba(51, 51, 51, 0.4);
  font-weight: 500;
  font-size: 14px;
  gap: 12px;
}

.main-chart {
  border-radius: 4px;
  height: 100%;
  display: flex;
  flex-direction: column;

  .no-drag {
    display: flex;
  }
}

.chart-active {
  border-color: #2c5ad8 !important;
}

.chart-title {
  display: flex;
  width: 100%;
  justify-content: space-between;
  align-items: center;
  gap: 16px;

  &_text {
    max-width: 200px;
    :deep .c-label {
      font-weight: 700 !important;
    }

    .active-title {
      &:hover {
        text-decoration: underline;
      }
    }
  }

  .chart-max-value {
    display: flex;

    span {
      font-size: 13px;
      line-height: 16px;
      font-weight: 500;
      color: #2f3237;
    }

    .metric-unit {
      margin-left: 4px;
    }
  }
}

.chart-detail {
  &__main {
    box-shadow: 0px 8px 15px 2px #22252a26, 0px 0px 4px 0px #22252a33, 0px -1px 4px 0px #22252a26;
    border-radius: 4px;
  }
}

.detail-chart {
  position: absolute;
  width: 1210px;
  height: 280px;
  z-index: 2;
  background-color: #ffffff;
}

.chart-thumnail-item {
  padding: 0;
  border: 1px solid #e3e3e3;
}

.chart-detail-item {
  padding: 8px 16px 16px 16px;
  border: 1px solid #e3e3e3;

  .chart-detail-content {
    display: flex;
    align-items: flex-end;
    height: 100%;
  }

  .chart-title {
    .chart-title_text > .c-label {
      font-size: 14px !important;
    }

    .chart-max-value > .until > span {
      font-size: 16px !important;
    }
  }
}

.chart-thumnail-title {
  padding: 10px;
}

.chart-detail-content {
  height: 100%;
}

.chart-detail {
  width: 100%;
}

.footer-detail {
  display: flex;
  flex-direction: row-reverse;
  padding-top: 16px;
  border-top: 1px solid #e3e4e8;
}

.dropdown-group {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  margin: 0;
  gap: 16px;

  .lego-drop-holder {
    display: flex !important;
  }

  .object-check {
    width: 180px !important;
  }
}

.dropdown-check-wrapper {
  padding: 0 12px;
}

.statistical-type-wrapper {
  &:hover {
    background-color: #eff1fb;
  }
}

.hover-style :hover {
  background-color: #eff1fb;
}

.button-wrapper {
  padding: 10px 16px !important;
  height: auto !important;

  &:hover {
    background-color: #eff1fb;
  }
}

.add-widget-button {
  cursor: pointer;
}

.button-icon {
  display: flex;
}

.button-icon_text {
  font-size: 14px !important;
  line-height: 16px !important;
  margin-left: 12px !important;
  vertical-align: middle !important;
}

:deep .menu-wrapper {
  min-width: 262px;
}

:deep .lego-icon.opus-icon__add {
  width: unset !important;
}

// fix maxwidth chart-title_text
.chart-detail-title {
  .chart-title_text {
    max-width: 500px;
  }
}
</style>
